import React, { useState, useEffect } from "react";

import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import { Table, Button, Container, Modal, Form, ButtonGroup, Pagination, Spinner, Tabs, Tab } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import ErrorModal from './ErrorModal';
import ApiRegistryManagerModal from './ApiRegistryManagerModal';

// Leeres Produkt-Formular inkl. Marketing-/Design-Felder.
const emptyServiceForm = {
  service_name: '',
  service_in_url: '',
  service_out_url: '',
  participant_type: 'BOTH',
  description: '',
  ad_text: '',
  ad_image_url: '',
  ad_media_url: '',
  icon_url: '',
  accent_color: '',
  api_id: '',
  sytdomains_product_key: '',
  sytdomains_voucher_type: 'product',
  sytdomains_discount_type: 'percent',
  sytdomains_discount_value: '',
  // Gewaehlte Transportarten (Mehrfachauswahl, IDs aus den Stammdaten).
  transport_type_ids: [],
  rec_date: '',
};

// Wandelt einen DB-Datensatz (mit moeglichen null-Werten) in ein vollstaendig
// kontrolliertes Formularobjekt um (vermeidet controlled/uncontrolled-Warnungen).
const normalizeService = (s = {}) => ({
  ...emptyServiceForm,
  ...s,
  service_name: s.service_name || '',
  service_in_url: s.service_in_url || '',
  service_out_url: s.service_out_url || '',
  participant_type: s.participant_type === 'HUMAN' || s.participant_type === 'AI' ? s.participant_type : 'BOTH',
  description: s.description || '',
  ad_text: s.ad_text || '',
  ad_image_url: s.ad_image_url || '',
  ad_media_url: s.ad_media_url || '',
  icon_url: s.icon_url || '',
  accent_color: s.accent_color || '',
  api_id: s.api_id == null ? '' : s.api_id,
  sytdomains_product_key: s.sytdomains_product_key || '',
  sytdomains_voucher_type: s.sytdomains_voucher_type === 'discount' ? 'discount' : 'product',
  sytdomains_discount_type: s.sytdomains_discount_type === 'fixed' ? 'fixed' : 'percent',
  sytdomains_discount_value: s.sytdomains_discount_value == null ? '' : `${s.sytdomains_discount_value}`,
  // Die API liefert die Zuordnung als Objektliste; fuer das Formular werden
  // daraus die reinen IDs uebernommen.
  transport_type_ids: Array.isArray(s.transportTypes)
    ? s.transportTypes.map((t) => t.transport_type_id)
    : (Array.isArray(s.transport_type_ids) ? s.transport_type_ids : []),
});

const BoardServices = ({
  showcaseId = null,
  title = "Dienstübersicht",
  unassignedOnly = false,
  showcases = [],
  allowCreate = true,
  reloadSignal = 0,
  onMutate = null,
}) => {
  const [services, setServices] = useState([]);
  // Pro Zeile gewaehlte Ziel-Produktart fuer die Zuordnung (service_id -> showcase_id).
  const [assignTargets, setAssignTargets] = useState({});
  const [assigningId, setAssigningId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [deleteState, setDeleteState] = useState(false);
  const [errors, setErrors] = useState({}); // Error state
  const [apierrors, setApiErrors] = useState('');// Error state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterTerm, setFilterTerm] = useState('');

  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(' Lade, bitte warten...');
  const [showModal, setShowModal] = useState(false);
  const [errorCode, setApiErrorCode] = useState('');// Error state

  // API-Administration: Registry-Liste, Verwaltungs-Modal, Tab und Testergebnis.
  const [apiList, setApiList] = useState([]);
  // Stammdaten der Transportarten fuer die Mehrfachauswahl im Dienst-Dialog.
  const [transportTypeList, setTransportTypeList] = useState([]);
  const [showApiManager, setShowApiManager] = useState(false);
  const [editActiveTab, setEditActiveTab] = useState('allgemein');
  const [apiTestResult, setApiTestResult] = useState(null);
  


  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Set items per page


  const [formData, setFormData] = useState({ ...emptyServiceForm });

  const validateForm = () => {
    const newErrors = {};
  
    // Validate service_name
    if (!formData.service_name?.trim()) {
      newErrors.service_name = 'Service Name is required';
    }
  
    // Validate service_out_url (optional: nur Formatpruefung, wenn ausgefuellt)
    if (formData.service_out_url?.trim() && !/^https?:\/\/[^\s$.?#].[^\s]*$/i.test(formData.service_out_url)) {
      newErrors.service_out_url = 'Service OUT URL must be a valid URL';
    }
  
    // Validate service_in_url (optional: nur Formatpruefung, wenn ausgefuellt)
    if (formData.service_in_url?.trim() && !/^https?:\/\/[^\s$.?#].[^\s]*$/i.test(formData.service_in_url)) {
      newErrors.service_in_url = 'Service IN URL must be a valid URL';
    }

    // Marketing-/Design-URLs (optional): nur Formatpruefung, wenn ausgefuellt.
    const urlRe = /^https?:\/\/[^\s]+$/i;
    ['ad_image_url', 'ad_media_url', 'icon_url'].forEach((key) => {
      if (formData[key]?.trim() && !urlRe.test(formData[key].trim())) {
        newErrors[key] = 'Bitte eine gültige URL (http/https) angeben.';
      }
    });

    // Designfarbe (optional): Hex-Format pruefen, wenn ausgefuellt.
    if (formData.accent_color?.trim() && !/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(formData.accent_color.trim())) {
      newErrors.accent_color = 'Bitte einen Hex-Farbwert angeben (z. B. #1a73e8).';
    }

    // Produktschluessel sytdomains (optional): A-Z, 0-9, _ und - (max. 64).
    if (formData.sytdomains_product_key?.trim() && !/^[A-Za-z0-9_-]{1,64}$/.test(formData.sytdomains_product_key.trim())) {
      newErrors.sytdomains_product_key = 'Nur A-Z, 0-9, _ und - erlaubt (max. 64 Zeichen).';
    }

    // Rabatt-Voucher: Rabattwert ist Pflicht und muss > 0 sein.
    if (formData.sytdomains_product_key?.trim() && formData.sytdomains_voucher_type === 'discount') {
      const v = Number(String(formData.sytdomains_discount_value || '').replace(',', '.'));
      if (!Number.isFinite(v) || v <= 0) {
        newErrors.sytdomains_discount_value = 'Bitte einen Rabattwert größer 0 angeben.';
      } else if (formData.sytdomains_discount_type === 'percent' && v > 100) {
        newErrors.sytdomains_discount_value = 'Prozent-Rabatt darf höchstens 100 sein.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if no errors
  };
  

  useEffect(() => {
    
    UserService.getServiceBoard().then(
      (response) => {
        setServices(response.data);
      },
      (error) => {
        const _content =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();
          if (error.response) {
            // Server responded with a status other than 200 range
            setApiErrors(`Error: ${error.name} - ${error.response.data.message}-${error.code}`);
            setApiErrorCode(`Error: ${error.name} - ${error.code}`);
          } else if (error.request) {
            // Request was made, but no response was received
            setApiErrors('Error: No response received from the server.');
            setApiErrorCode(`Error: ${error.name} - ${error.code}`);
          } else {
            // Other types of errors
            setApiErrors(`Error: ${error.response.data.message}`);
            setApiErrorCode(`Error: ${error.name} - ${error.code}`);
          } 
          setServices(_content);

        if (error.response && error.response.status === 401) {
          EventBus.dispatch("logout");
        }
        
      
});
  }, [deleteState, reloadSignal]);

  // API-Registry laden (fuer das Zuordnungs-Dropdown im Bearbeiten-Modal).
  const reloadApis = () => {
    UserService.getApiRegistry().then(
      (response) => setApiList(Array.isArray(response.data) ? response.data : []),
      () => setApiList([])
    );
  };

  useEffect(() => {
    reloadApis();
  }, [reloadSignal]);

  // Transportarten laden. Die Liste wird ausschliesslich aus den Stammdaten
  // gespeist; im Code stehen keine festen Bezeichnungen.
  useEffect(() => {
    UserService.getTransportTypesBoard().then(
      (response) => setTransportTypeList(Array.isArray(response.data) ? response.data : []),
      () => setTransportTypeList([])
    );
  }, [reloadSignal]);


// Optional auf einen Showcase-Container einschraenken (additiv; ohne Prop = altes Verhalten)
const baseServices = Array.isArray(services)
    ? (showcaseId != null
        ? services.filter((vo) => vo.showcase_id === showcaseId)
        : (unassignedOnly
            ? services.filter((vo) => vo.showcase_id == null)
            : services))
    : [];

// Filter the mainvos based on the filter input
const filteredMainVOs = baseServices.filter((vo) =>
        (vo.service_name && vo.service_name.toLowerCase().includes(filterTerm.toLowerCase())) ||
    (vo.service_in_url && vo.service_in_url.toLowerCase().includes(filterTerm.toLowerCase())) ||
    (vo.service_out_url && vo.service_out_url.toLowerCase().includes(filterTerm.toLowerCase())) ||
    (vo.rec_date && vo.rec_date.includes(filterTerm))
      );

// Get the current page's data
const indexOfLastItem = currentPage * itemsPerPage;
const indexOfFirstItem = indexOfLastItem - itemsPerPage;
const currentMainVOs = filteredMainVOs.slice(indexOfFirstItem, indexOfLastItem);

 // Handle pagination
const handlePageChange = (pageNumber) => {
  setCurrentPage(pageNumber);
};

// Sorting function
const handleSort = (key) => {
  let direction = 'asc';
  if (sortConfig.key === key && sortConfig.direction === 'asc') {
    direction = 'desc';
  }
  setSortConfig({ key, direction });
};


// Sort the filtered data
const sortedMainVOs = [...filteredMainVOs].sort((a, b) => {
  if (a[sortConfig.key] < b[sortConfig.key]) {
    return sortConfig.direction === 'asc' ? -1 : 1;
  }
  if (a[sortConfig.key] > b[sortConfig.key]) {
    return sortConfig.direction === 'asc' ? 1 : -1;
  }
  return 0;
});


// Total pages calculation
const totalPages = Math.ceil(filteredMainVOs.length / itemsPerPage);


  const handleEditClick = (service) => {
    setApiErrors(null);
    setApiErrorCode(null);
    setSelectedService(service)
    setFormData(normalizeService(service));
    setEditActiveTab('allgemein');
    setApiTestResult(null);
    setShowEditModal(true);
  };

  // Verbindungstest fuer die aktuell im Dropdown gewaehlte API.
  const handleTestSelectedApi = async () => {
    const id = formData.api_id;
    if (!id) {
      setApiTestResult({ ok: false, text: 'Bitte zuerst eine API auswaehlen.' });
      return;
    }
    setApiTestResult({ loading: true });
    try {
      const res = await UserService.testApiRegistry(id);
      const d = res.data || {};
      setApiTestResult({
        ok: !!d.ok,
        text: d.ok
          ? `Verbindung OK (${d.latency_ms} ms)`
          : `Fehlgeschlagen (HTTP ${d.http_status != null ? d.http_status : '-'})`,
      });
    } catch (error) {
      const d = (error.response && error.response.data) || {};
      setApiTestResult({ ok: false, text: d.message || error.message || 'Verbindung fehlgeschlagen.' });
    }
  };

  const handleCreateClick = () => {
    setApiErrors(null);
    setApiErrorCode(null);
    setFormData({ ...emptyServiceForm });
    setShowCreateModal(true);
  };

  const isValidDate = (date) => {
    return !isNaN(Date.parse(date));
  };

  const handleDeleteClick = async (id,e) => {
    e.preventDefault();
    setApiErrors(null);
    setApiErrorCode(null);
    try {
      setLoadingText('Daten werden gelöscht, bitte warten...');
      setLoading(true);
      await UserService.deleteServiceBoard(id);
      setDeleteState(prev => !prev);
      console.info(deleteState);
    } catch (error) {
      setApiErrors(`Error: ${error.name}-${error.code} - ${error.response.data.message}-${error.response.data.error}`);
      setApiErrorCode(`Error: ${error.name} - ${error.code}`);
      setShowModal(true);
      console.error('Error deleting Service:', error);
    }
    finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return; 
    try {
      setLoadingText('Daten werden aktualisiert, bitte warten...');
      setLoading(true);
      formData.rec_date = new Date();
      await UserService.updateServiceBoard(selectedService.service_id,formData);
      setDeleteState(prev => !prev);
      setShowEditModal(false);
    } catch (error) {
      setApiErrors(`Error: ${error.name} - ${error.response.data.message}-${error.code}`);
      setApiErrorCode(`Error: ${error.name} - ${error.code}`);
      console.error('Error creating Service:', error);
      setShowModal(true);
    }
    finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return; 
    try {
      setLoadingText('Daten werden erstellt, bitte warten...');
      setLoading(true);
      formData.rec_date = new Date();
      const recDate = formData.rec_date;
      const validDate = isValidDate(recDate) ? recDate : null;     
      formData.rec_date = validDate;
      // Beim Anlegen innerhalb eines Showcase-Containers die Zuordnung setzen.
      if (showcaseId != null) {
        formData.showcase_id = showcaseId;
      }
      await UserService.createServiceBoard(formData);
      setDeleteState(prev => !prev);
      setShowCreateModal(false);
      
    } catch (error) {
      setApiErrors(`Error: ${error.name} - ${error.response.data.message}-${error.code}`);
      setApiErrorCode(`Error: ${error.name} - ${error.code}`);
      console.error('Error creating Service:', error);
      setShowModal(true);
    }
    finally {
      setLoading(false);
    }
  };

  // Ordnet ein bestehendes Produkt einer Produktart (Showcase) zu. Partielle
  // Aktualisierung: es wird ausschliesslich showcase_id gesetzt.
  const handleAssign = async (service) => {
    const target = assignTargets[service.service_id];
    if (!target) return;
    setApiErrors(null);
    setApiErrorCode(null);
    try {
      setAssigningId(service.service_id);
      setLoadingText('Zuordnung wird gespeichert, bitte warten...');
      setLoading(true);
      await UserService.updateServiceBoard(service.service_id, { showcase_id: parseInt(target, 10) });
      if (typeof onMutate === 'function') {
        onMutate();
      } else {
        setDeleteState((prev) => !prev);
      }
    } catch (error) {
      setApiErrors(`Error: ${error.name} - ${error.response?.data?.message}-${error.code}`);
      setApiErrorCode(`Error: ${error.name} - ${error.code}`);
      setShowModal(true);
      console.error('Error assigning Service:', error);
    } finally {
      setLoading(false);
      setAssigningId(null);
    }
  };

  const handleBlur = () => {
    validateForm();
 };

 const handleChange = (e) => {
   setFormData({
     ...formData,
     [e.target.name]: e.target.value
   });
 validateForm();
 };

 const handleCloseModal = () => setShowModal(false);

  // Eignung des Dienstes (Mensch/KI) – gemeinsame Radio-Gruppe fuer beide Modals.
  const renderParticipantTypeField = (idSuffix) => (
    <Form.Group controlId={`formParticipantType${idSuffix}`} className="mb-3">
      <Form.Label className="fw-semibold">Geeignet für</Form.Label>
      <div className="myn-participant-options">
        {[
          { value: 'HUMAN', label: 'Mensch' },
          { value: 'AI', label: 'KI' },
          { value: 'BOTH', label: 'Mensch + KI' },
        ].map((opt) => (
          <Form.Check
            key={opt.value}
            type="radio"
            id={`participantType${idSuffix}-${opt.value}`}
            name="participant_type"
            value={opt.value}
            label={opt.label}
            checked={formData.participant_type === opt.value}
            onChange={handleChange}
            className="myn-circle-option"
          />
        ))}
      </div>
      <Form.Text muted>
        Legt fest, ob der Dienst für Menschen, KI-Teilnehmer oder beide vorgesehen ist.
      </Form.Text>
    </Form.Group>
  );

  // Eine Transportart an- oder abwaehlen (Mehrfachauswahl).
  const handleTransportTypeToggle = (transportTypeId) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.transport_type_ids) ? prev.transport_type_ids : [];
      const next = current.includes(transportTypeId)
        ? current.filter((id) => id !== transportTypeId)
        : [...current, transportTypeId];
      return { ...prev, transport_type_ids: next };
    });
  };

  // Transportarten des Dienstes – Mehrfachauswahl fuer beide Modals. Die Werte
  // stammen vollstaendig aus den Stammdaten (Einstellungen → Transportarten).
  // Bereits zugeordnete, inzwischen deaktivierte Eintraege bleiben sichtbar,
  // damit eine bestehende Zuordnung nicht unbemerkt verschwindet.
  const renderTransportTypeField = (idSuffix) => {
    const selectedIds = Array.isArray(formData.transport_type_ids) ? formData.transport_type_ids : [];
    const visible = transportTypeList.filter(
      (t) => t.is_active || selectedIds.includes(t.transport_type_id)
    );

    return (
      <Form.Group controlId={`formTransportTypes${idSuffix}`} className="mt-3">
        <Form.Label className="fw-semibold">Transportart</Form.Label>
        {visible.length === 0 ? (
          <div className="text-muted">
            Es sind noch keine Transportarten hinterlegt. Bitte zuerst unter
            „Stammdaten → Transportarten“ anlegen.
          </div>
        ) : (
          <div className="myn-transport-options">
            {visible.map((t) => (
              <Form.Check
                key={t.transport_type_id}
                type="checkbox"
                id={`transportType${idSuffix}-${t.transport_type_id}`}
                label={t.is_active ? t.transport_type_name : `${t.transport_type_name} (inaktiv)`}
                checked={selectedIds.includes(t.transport_type_id)}
                onChange={() => handleTransportTypeToggle(t.transport_type_id)}
                className="myn-transport-option myn-circle-option"
              />
            ))}
          </div>
        )}
        <Form.Text muted>
          Mehrfachauswahl: Legt fest, auf welchen Wegen dieses Produkt ausgeliefert
          werden kann. Wird ausschließlich eine physikalische Transportart gewählt,
          bleibt der Dienst der Auslieferung über Vertriebspartner vorbehalten.
        </Form.Text>
      </Form.Group>
    );
  };

  // Gemeinsame sytdomains-Voucher-Felder (Typ + Rabatt) fuer beide Modals.
  // Nur relevant, wenn ein Produktschluessel gesetzt ist.
  const renderSytdomainsVoucherFields = (idSuffix) => (
    !formData.sytdomains_product_key?.trim() ? null : (
      <>
        <Form.Group controlId={`formSytdomainsVoucherType${idSuffix}`} className="mt-3">
          <Form.Label>Voucher-Typ (sytdomains)</Form.Label>
          <Form.Control
            as="select"
            name="sytdomains_voucher_type"
            value={formData.sytdomains_voucher_type}
            onChange={handleChange}
          >
            <option value="product">Produkt – Leistung ist prepaid einlösbar</option>
            <option value="discount">Rabatt – Code gibt Rabatt im sytdomains-Checkout</option>
          </Form.Control>
        </Form.Group>
        {formData.sytdomains_voucher_type === 'discount' && (
          <div className="d-flex gap-2">
            <Form.Group controlId={`formSytdomainsDiscountType${idSuffix}`} className="mt-2" style={{ flex: 1 }}>
              <Form.Label>Rabattart</Form.Label>
              <Form.Control
                as="select"
                name="sytdomains_discount_type"
                value={formData.sytdomains_discount_type}
                onChange={handleChange}
              >
                <option value="percent">Prozent (%)</option>
                <option value="fixed">Festbetrag (€)</option>
              </Form.Control>
            </Form.Group>
            <Form.Group controlId={`formSytdomainsDiscountValue${idSuffix}`} className="mt-2" style={{ flex: 1 }}>
              <Form.Label>Rabattwert</Form.Label>
              <Form.Control
                type="text"
                placeholder={formData.sytdomains_discount_type === 'fixed' ? 'z. B. 10,00' : 'z. B. 20'}
                name="sytdomains_discount_value"
                value={formData.sytdomains_discount_value}
                onChange={handleChange}
                isInvalid={!!errors.sytdomains_discount_value}
              />
              <Form.Control.Feedback type="invalid">
                {errors.sytdomains_discount_value}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
        )}
      </>
    )
  );

  // Gemeinsame Marketing-/Design-Felder fuer Erstellen- und Bearbeiten-Modal.
  const renderMarketingFields = () => (
    <div className="myn-service-section">
      <div className="myn-service-section__title">4. Beschreibung, Werbung &amp; Erscheinungsbild</div>
      <div className="row g-3">
        <div className="col-12 col-md-6">
          <Form.Group controlId="formServiceDescription">
            <Form.Label>Beschreibung</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Produktbeschreibung"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
            />
          </Form.Group>
        </div>

        <div className="col-12 col-md-6">
          <Form.Group controlId="formServiceAdText">
            <Form.Label>Werbung (Text)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Werbe-/Marketingtext (wird als Klartext angezeigt)"
              name="ad_text"
              value={formData.ad_text || ''}
              onChange={handleChange}
            />
          </Form.Group>
        </div>

        <div className="col-12 col-md-6">
          <Form.Group controlId="formServiceAdImage">
            <Form.Label>Werbung – Bild-URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://..."
              name="ad_image_url"
              value={formData.ad_image_url || ''}
              onChange={handleChange}
              isInvalid={!!errors.ad_image_url}
            />
            <Form.Control.Feedback type="invalid">{errors.ad_image_url}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-12 col-md-6">
          <Form.Group controlId="formServiceAdMedia">
            <Form.Label>Werbung – Video-/Link-URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://..."
              name="ad_media_url"
              value={formData.ad_media_url || ''}
              onChange={handleChange}
              isInvalid={!!errors.ad_media_url}
            />
            <Form.Control.Feedback type="invalid">{errors.ad_media_url}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-12 col-md-6">
          <Form.Group controlId="formServiceIcon">
            <Form.Label>Icon-/Logo-URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://..."
              name="icon_url"
              value={formData.icon_url || ''}
              onChange={handleChange}
              isInvalid={!!errors.icon_url}
            />
            <Form.Control.Feedback type="invalid">{errors.icon_url}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="col-12 col-md-6">
          <Form.Group controlId="formServiceColor">
            <Form.Label>Designfarbe (optional)</Form.Label>
            <div className="d-flex gap-2 align-items-center">
              <Form.Control
                type="color"
                name="accent_color"
                value={/^#([0-9a-f]{6})$/i.test(formData.accent_color || '') ? formData.accent_color : '#1a73e8'}
                onChange={handleChange}
                title="Farbe wählen"
                style={{ maxWidth: 60, height: 38, padding: 2 }}
              />
              <Form.Control
                type="text"
                placeholder="#1a73e8"
                name="accent_color"
                value={formData.accent_color || ''}
                onChange={handleChange}
                isInvalid={!!errors.accent_color}
              />
            </div>
            <Form.Control.Feedback type="invalid" style={{ display: errors.accent_color ? 'block' : 'none' }}>
              {errors.accent_color}
            </Form.Control.Feedback>
          </Form.Group>
        </div>
      </div>
    </div>
  );

  return (
    <Container>
      <h1 className="mt-5">{title}</h1>
      {/* Display error if it exists */}
      <ErrorModal show={showModal} handleClose={handleCloseModal} errorCode={errorCode} errorMessage={apierrors} />

      {allowCreate && (
        <Button variant="info" onClick={() => handleCreateClick()} className="mr-2 mb-2">
                  Erstellen
        </Button>
      )}
      {loading && (
        <div className="spinner-overlay">
         <div className="spinner-background">
          <Spinner animation="border" role="status" className="spinner-border">
            <span className="sr-only">Lade...</span>
          </Spinner>
          <div className="spinner-text">...{loadingText}</div>
          </div>
        </div>
      )}
      {/* Filter Input */}
      <Form.Group controlId="formBasicSearch">
        <Form.Control
          type="text"
          placeholder="Suche nach Dienstname, ID und Datum"
          value={filterTerm}
          onChange={(e) => setFilterTerm(e.target.value)}
        />
      </Form.Group>
      {/* Table */}
      <Table responsive striped bordered hover className="mt-4">
        <thead>
          <tr>
            {/* Sortable column headers */}
            <th onClick={() => handleSort('service_id')} style={{ cursor: 'pointer' }}>
              ID {sortConfig.key === 'service_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('service_name')} style={{ cursor: 'pointer' }}>
              Dienstname {sortConfig.key === 'service_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th>Beschreibung</th>
            <th>Werbung</th>
            <th onClick={() => handleSort('service_in_url')} style={{ cursor: 'pointer' }}>
              Dienst IN-URL {sortConfig.key === 'service_in_url' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('service_out_url')} style={{ cursor: 'pointer' }}>
              Dienst AUS-URL {sortConfig.key === 'service_out_url' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th>Transportart</th>
            <th onClick={() => handleSort('rec_date')} style={{ cursor: 'pointer' }}>
              Erstelldatum {sortConfig.key === 'rec_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            
            {showcases.length > 0 && <th>Produktart zuordnen</th>}
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {sortedMainVOs.map((service) => (
     
            <tr key={service.service_id}>
              <td>{service.service_id}</td>
              <td>
                <div className="d-flex align-items-center gap-2">
                  {service.icon_url && (
                    <img
                      src={service.icon_url}
                      alt=""
                      style={{ width: 24, height: 24, objectFit: 'contain' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  {service.accent_color && (
                    <span
                      title={service.accent_color}
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: service.accent_color,
                        border: '1px solid #ccc',
                        flex: '0 0 auto',
                      }}
                    />
                  )}
                  <span>{service.service_name}</span>
                </div>
              </td>
              <td style={{ maxWidth: 220, whiteSpace: 'normal' }} title={service.description || ''}>
                {service.description
                  ? (service.description.length > 80 ? `${service.description.slice(0, 80)}…` : service.description)
                  : <span className="text-muted">–</span>}
              </td>
              <td style={{ minWidth: 140 }}>
                <div className="d-flex align-items-center gap-2">
                  {service.ad_image_url && (
                    <img
                      src={service.ad_image_url}
                      alt=""
                      style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <div>
                    {service.ad_text && (
                      <div title={service.ad_text} style={{ maxWidth: 160, whiteSpace: 'normal' }}>
                        {service.ad_text.length > 50 ? `${service.ad_text.slice(0, 50)}…` : service.ad_text}
                      </div>
                    )}
                    {service.ad_media_url && (
                      <a href={service.ad_media_url} target="_blank" rel="noreferrer">Link</a>
                    )}
                    {!service.ad_text && !service.ad_image_url && !service.ad_media_url && (
                      <span className="text-muted">–</span>
                    )}
                  </div>
                </div>
              </td>
              <td>{service.service_in_url}</td>
              <td>{service.service_out_url}</td>
              <td style={{ maxWidth: 180, whiteSpace: 'normal' }}>
                {Array.isArray(service.transportTypes) && service.transportTypes.length > 0
                  ? service.transportTypes.map((t) => t.transport_type_name).join(', ')
                  : <span className="text-muted">–</span>}
              </td>
              <td>{service.rec_date}</td>
              
              {showcases.length > 0 && (
                <td>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Select
                      size="sm"
                      value={assignTargets[service.service_id] || ''}
                      onChange={(e) =>
                        setAssignTargets({ ...assignTargets, [service.service_id]: e.target.value })
                      }
                    >
                      <option value="">– Produktart wählen –</option>
                      {showcases.map((sc) => (
                        <option key={sc.showcase_id} value={sc.showcase_id}>
                          {sc.title}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      size="sm"
                      variant="success"
                      disabled={!assignTargets[service.service_id] || assigningId === service.service_id}
                      onClick={() => handleAssign(service)}
                      title="Produkt dieser Produktart zuordnen"
                    >
                      Zuordnen
                    </Button>
                  </div>
                </td>
              )}
              <td>
              <ButtonGroup>
                <Button variant="warning" onClick={() => handleEditClick(service)} className="mr-2" title="Bearbeiten" aria-label="Bearbeiten">
                  <FaEdit />
                </Button>
                <Button variant="danger" onClick={(e) => handleDeleteClick(service.service_id,e)} title="Löschen" aria-label="Löschen">
                  <FaTrash />
                </Button>
                </ButtonGroup>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {unassignedOnly && baseServices.length === 0 && (
        <p className="text-success mb-2">Alle Produkte sind einer Produktart zugeordnet.</p>
      )}
      <Pagination>
        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

        {[...Array(totalPages)].map((_, index) => (
          <Pagination.Item
            key={index}
            active={index + 1 === currentPage}
            onClick={() => handlePageChange(index + 1)}
          >
            {index + 1}
          </Pagination.Item>
        ))}

        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>
      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" className="myn-service-modal">
        <Modal.Header closeButton>
          <Modal.Title>Neuen Dienst erstellen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateSubmit}>
            <div className="myn-service-section">
              <div className="myn-service-section__title">1. Basisdaten &amp; Weiterleitungs-URLs</div>
              <Form.Group controlId="formServiceNameCreate" className="mb-3">
                <Form.Label>Dienstname</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Dienstnamen eingeben"
                  name="service_name"
                  value={formData.service_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!errors.service_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.service_name}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <Form.Group controlId="formServiceInUrlCreate">
                    <Form.Label>Dienst IN-URL</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Dienst IN-URL eingeben"
                      name="service_in_url"
                      value={formData.service_in_url}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={!!errors.service_in_url}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.service_in_url}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="col-12 col-md-6">
                  <Form.Group controlId="formServiceOutUrlCreate">
                    <Form.Label>Dienst AUS-URL</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Dienst AUS-URL eingeben"
                      name="service_out_url"
                      value={formData.service_out_url}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={!!errors.service_out_url}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.service_out_url}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              </div>
            </div>

            <div className="myn-service-section">
              <div className="myn-service-section__title">2. Zielgruppe &amp; Auslieferung</div>
              {renderParticipantTypeField('Create')}
              {renderTransportTypeField('Create')}
            </div>

            <div className="myn-service-section">
              <div className="myn-service-section__title">3. Partner-Integration (QR-Vermarktung)</div>
              <Form.Group controlId="formSytdomainsKeyCreate">
                <Form.Label>Produktschlüssel sytdomains (QR-Vermarktung)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="z. B. SYTHOSTING (leer = kein Push)"
                  name="sytdomains_product_key"
                  value={formData.sytdomains_product_key}
                  onChange={handleChange}
                  isInvalid={!!errors.sytdomains_product_key}
                />
                <Form.Text muted>
                  Chargen-Codes dieses Dienstes werden zusätzlich an sytdomains.com gepusht (Tarif mit gleichem Produkt-Schlüssel erforderlich).
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.sytdomains_product_key}
                </Form.Control.Feedback>
              </Form.Group>

              {renderSytdomainsVoucherFields('Create')}
            </div>

            {renderMarketingFields()}

            <div className="d-flex justify-content-end mt-3">
              <Button variant="primary" type="submit" className="px-4" disabled={loading}>
                {loading ? 'Wird gespeichert...' : 'Dienst speichern'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" className="myn-service-modal">
        <Modal.Header closeButton>
          <Modal.Title>Dienst bearbeiten</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Tabs
              activeKey={editActiveTab}
              onSelect={(k) => setEditActiveTab(k || 'allgemein')}
              className="mb-3"
            >
              <Tab eventKey="allgemein" title="Allgemein">
                <div className="myn-service-section">
                  <div className="myn-service-section__title">1. Basisdaten &amp; Weiterleitungs-URLs</div>
                  <Form.Group controlId="formServiceNameEdit" className="mb-3">
                    <Form.Label>Dienstname</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Dienstnamen eingeben"
                      name="service_name"
                      value={formData.service_name}
                      onChange={handleChange}
                      isInvalid={!!errors.service_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.service_name}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <Form.Group controlId="formServiceInUrlEdit">
                        <Form.Label>Dienst IN-URL</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Dienst IN-URL eingeben"
                          name="service_in_url"
                          value={formData.service_in_url}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={!!errors.service_in_url}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.service_in_url}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>
                    <div className="col-12 col-md-6">
                      <Form.Group controlId="formServiceOutUrlEdit">
                        <Form.Label>Dienst AUS-URL</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Dienst AUS-URL eingeben"
                          name="service_out_url"
                          value={formData.service_out_url}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={!!errors.service_out_url}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.service_out_url}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>
                  </div>
                </div>

                <div className="myn-service-section">
                  <div className="myn-service-section__title">2. Zielgruppe &amp; Auslieferung</div>
                  {renderParticipantTypeField('Edit')}
                  {renderTransportTypeField('Edit')}
                </div>

                <div className="myn-service-section">
                  <div className="myn-service-section__title">3. Partner-Integration (QR-Vermarktung)</div>
                  <Form.Group controlId="formSytdomainsKeyEdit">
                    <Form.Label>Produktschlüssel sytdomains (QR-Vermarktung)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="z. B. SYTHOSTING (leer = kein Push)"
                      name="sytdomains_product_key"
                      value={formData.sytdomains_product_key}
                      onChange={handleChange}
                      isInvalid={!!errors.sytdomains_product_key}
                    />
                    <Form.Text muted>
                      Chargen-Codes dieses Dienstes werden zusätzlich an sytdomains.com gepusht (Tarif mit gleichem Produkt-Schlüssel erforderlich).
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                      {errors.sytdomains_product_key}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {renderSytdomainsVoucherFields('Edit')}
                </div>

                {renderMarketingFields()}
              </Tab>

              <Tab eventKey="api" title="API">
                <div className="myn-service-section">
                  <div className="myn-service-section__title">API-Zuordnung &amp; Verbindungstest</div>
                  <Form.Group controlId="formServiceApi">
                    <Form.Label>Zugeordnete API</Form.Label>
                    <Form.Select
                      name="api_id"
                      value={formData.api_id || ''}
                      onChange={(e) => { handleChange(e); setApiTestResult(null); }}
                    >
                      <option value="">– keine API –</option>
                      {apiList.map((api) => (
                        <option key={api.api_id} value={api.api_id} disabled={!api.enabled}>
                          {api.api_name}{api.provider ? ` (${api.provider})` : ''}{!api.enabled ? ' – inaktiv' : ''}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Pro Produkt kann genau eine API zugeordnet werden.
                    </Form.Text>
                  </Form.Group>

                  <div className="d-flex gap-2 mt-3 flex-wrap">
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => setShowApiManager(true)}
                    >
                      APIs verwalten
                    </Button>
                    <Button
                      variant="info"
                      type="button"
                      disabled={!formData.api_id || (apiTestResult && apiTestResult.loading)}
                      onClick={handleTestSelectedApi}
                    >
                      {apiTestResult && apiTestResult.loading ? 'Teste…' : 'Verbindung testen'}
                    </Button>
                  </div>

                  {apiTestResult && !apiTestResult.loading && (
                    <div className={apiTestResult.ok ? 'text-success mt-2 fw-semibold' : 'text-danger mt-2 fw-semibold'}>
                      {apiTestResult.text}
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>

            <div className="d-flex justify-content-end mt-3">
              <Button variant="primary" type="submit" className="px-4" disabled={loading}>
                {loading ? 'Wird aktualisiert...' : 'Dienst aktualisieren'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* API-Administration (Registry-Verwaltung) */}
      <ApiRegistryManagerModal
        show={showApiManager}
        onHide={() => { setShowApiManager(false); reloadApis(); }}
        onChanged={reloadApis}
      />
    </Container>
  );
};

export default BoardServices;